import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import SEOPageLayout from '../components/seo/SEOPageLayout';
import CloneSkillsBlock from '../components/CloneSkillsBlock';

const relatedLinks = [
  { to: '/memoire-entreprise-futur-travail', label: 'La mémoire d\'entreprise : le futur du travail' },
  { to: '/entreprise-interrogeable', label: 'Et si votre entreprise devenait interrogeable ?' },
  { to: '/perte-information-entreprise', label: 'Ce que la perte d\'information coûte à votre entreprise' },
];

const CloneCollaborateur = () => {
  return (
    <SEOPageLayout
      title={"Le clone collaborateur\u00a0: ce que \u00e7a signifie vraiment"}
      metaTitle="Clone m\u00e9moire collaborateur : qu'est-ce que \u00e7a veut dire ? | Cloneia"
      metaDescription="Pas un clone humain. Un clone de sa m\u00e9moire, de son ton et de ses r\u00e9flexes. D\u00e9couvrez comment Cloneia rend la connaissance de chaque collaborateur accessible."
      url="/clone-collaborateur-entreprise"
      readingTime={5}
      subtitle={"Sa m\u00e9moire, son ton, ses r\u00e9flexes \u2014 accessibles \u00e0 toute l\u2019\u00e9quipe, \u00e0 tout moment."}
      relatedLinks={relatedLinks}
    >
      <article className="space-y-12 text-[#a1a1aa] leading-relaxed" data-testid="seo-article-clone">

        {/* Section 1 - Clarifier */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            {"Cloner un collaborateur\u00a0: de quoi parle-t-on\u00a0?"}
          </h2>
          <p>
            {"Quand on parle de \u00ab\u00a0cloner un collaborateur\u00a0\u00bb, la premi\u00e8re r\u00e9action est souvent la surprise. Voire l\u2019inqui\u00e9tude."}
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            {"Soyons clairs\u00a0: il ne s\u2019agit ni d\u2019un clone humain, ni d\u2019une copie physique, ni d\u2019un outil de surveillance."}
          </p>
          <p className="mt-4">
            {"Un clone m\u00e9moire ne remplace pas la personne. Il ne la surveille pas. En revanche, il prolonge sa mani\u00e8re de travailler\u00a0: son ton, ses r\u00e9flexes, sa connaissance. Il rend cette expertise accessible \u00e0 toute l\u2019\u00e9quipe, m\u00eame quand le collaborateur n\u2019est pas disponible."}
          </p>
        </motion.div>

        {/* Section 2 - Expliquer */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            {"Ce que c\u2019est concr\u00e8tement"}
          </h2>
          <p>
            {"Chaque collaborateur accumule de la connaissance au fil de ses \u00e9changes. Des informations sur les clients, les projets, les process, les d\u00e9cisions. Et surtout\u00a0: une fa\u00e7on de faire qui lui est propre."}
          </p>
          <p className="mt-4">
            {"Cette connaissance vit dans ses emails, ses appels, ses messages. Elle est pr\u00e9cieuse. Et elle est, la plupart du temps, inaccessible aux autres."}
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            {"Un clone m\u00e9moire capte cette connaissance, adopte le style de communication du collaborateur, et la rend interrogeable instantan\u00e9ment."}
          </p>
          <div className="mt-6 p-6 rounded-xl border border-[#3b82f6]/20 bg-[#1a1a2e]/50">
            <p className="text-[#f5f5f5] font-medium mb-2">{"En une phrase"}</p>
            <p className="text-lg text-[#f5f5f5]">
              {"Cloneia cr\u00e9e un clone m\u00e9moire de chaque collaborateur\u00a0: sa connaissance, son ton, ses r\u00e9flexes \u2014 accessibles \u00e0 tout moment par toute l\u2019\u00e9quipe."}
            </p>
          </div>
        </motion.div>

        {/* Section 3 - Les 5 dimensions */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            {"Cinq capacit\u00e9s, un seul clone"}
          </h2>
          <p>
            {"Le clone m\u00e9moire ne se limite pas \u00e0 stocker de l\u2019information. Il op\u00e8re sur cinq dimensions\u00a0:"}
          </p>
          <div className="mt-6 space-y-4">
            {[
              { label: "M\u00e9moriser", desc: "Il retient chaque \u00e9change, chaque d\u00e9tail, sans rien oublier.", color: "#3b82f6" },
              { label: "Comprendre", desc: "Il d\u00e9tecte les signaux faibles, les tensions, les opportunit\u00e9s.", color: "#8b5cf6" },
              { label: "Incarner", desc: "Il adopte le ton et la mani\u00e8re de communiquer du collaborateur.", color: "#f59e0b" },
              { label: "Interagir", desc: "Il r\u00e9pond en langage naturel, comme si vous parliez \u00e0 la personne.", color: "#22c55e" },
              { label: "Proposer", desc: "Il anticipe les besoins et recommande la bonne action au bon moment.", color: "#ef4444" },
            ].map((cap) => (
              <div key={cap.label} className="flex items-start gap-4 p-4 rounded-xl border border-white/5 bg-[#0f0f17]/50">
                <div className="w-3 h-3 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: cap.color }} />
                <div>
                  <p className="text-[#f5f5f5] font-medium">{cap.label}</p>
                  <p className="text-sm mt-0.5">{cap.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[#f5f5f5] font-medium">
            {"Ce n\u2019est pas une base de donn\u00e9es. C\u2019est la m\u00e9moire vivante de votre organisation."}
          </p>
        </motion.div>

        {/* Section 4 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            {"Pourquoi c\u2019est n\u00e9cessaire"}
          </h2>
          <p>
            {"Aujourd\u2019hui, quand un collaborateur est absent, en cong\u00e9 ou quitte l\u2019entreprise, sa connaissance part avec lui."}
          </p>
          <p className="mt-4">
            {"Les clients ne sont plus suivis de la m\u00eame fa\u00e7on. Les dossiers perdent en contexte. Les nouveaux doivent tout r\u00e9apprendre de z\u00e9ro."}
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            {"Un clone m\u00e9moire met fin \u00e0 ce probl\u00e8me. La connaissance reste accessible, ind\u00e9pendamment de la disponibilit\u00e9 de la personne."}
          </p>
        </motion.div>

        {/* Section 5 - Concrètement */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            {"Comment \u00e7a fonctionne"}
          </h2>
          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#3b82f6] font-bold text-sm">1</span>
              </div>
              <div>
                <p className="text-[#f5f5f5] font-medium">{"Connexion automatique"}</p>
                <p className="text-sm mt-1">{"Cloneia se connecte aux outils existants de chaque collaborateur (email, t\u00e9l\u00e9phone, messagerie). Rien \u00e0 changer dans leur fa\u00e7on de travailler."}</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#3b82f6] font-bold text-sm">2</span>
              </div>
              <div>
                <p className="text-[#f5f5f5] font-medium">{"Captation et apprentissage"}</p>
                <p className="text-sm mt-1">{"L\u2019information est capt\u00e9e en continu. Le clone apprend le ton, les habitudes et les r\u00e9flexes du collaborateur. Z\u00e9ro saisie, z\u00e9ro effort."}</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#3b82f6] font-bold text-sm">3</span>
              </div>
              <div>
                <p className="text-[#f5f5f5] font-medium">{"Acc\u00e8s instantan\u00e9"}</p>
                <p className="text-sm mt-1">{"N\u2019importe qui dans l\u2019\u00e9quipe peut poser une question et obtenir une r\u00e9ponse dans le style du collaborateur, bas\u00e9e sur l\u2019ensemble de sa connaissance."}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 6 - Compétences du clone (bloc interactif) */}
        <CloneSkillsBlock />

        {/* Section 7 - Impact */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            {"Ce que \u00e7a change pour votre entreprise"}
          </h2>
          <p>
            {"Un nouveau collaborateur reprend un dossier en quelques secondes au lieu de quelques jours."}
          </p>
          <p className="mt-4">
            {"Un manager a une vision compl\u00e8te de l\u2019activit\u00e9 sans interrompre personne."}
          </p>
          <p className="mt-4">
            {"Un client est suivi de fa\u00e7on continue, dans le m\u00eame ton et avec le m\u00eame niveau de contexte, m\u00eame quand son interlocuteur habituel n\u2019est pas l\u00e0."}
          </p>
          <p className="mt-6 text-[#f5f5f5] font-medium text-lg">
            {"Le clone ne remplace pas le collaborateur. Il garantit que son expertise ne disparait jamais."}
          </p>
        </motion.div>

      </article>
    </SEOPageLayout>
  );
};

export default CloneCollaborateur;
